import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import {useCategorySettingsActions} from '../hooks/useCategorySettings';
import type {MarketCatalogue} from '../types/settingsTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const FRESHNESS = {fresh: ['Fresh', 'success'], stale: ['Stale', 'warning'], empty: ['No data', 'default']} as const;
const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});

/**
 * Market indices: the providers, every series in the shared catalogue (switch on or off) and Refresh now. Customer
 * uploads stay with their databag and are not listed here.
 */
export const MarketDataSettings: React.FC<{catalogue: MarketCatalogue}> = ({catalogue}) => {
    const actions = useCategorySettingsActions();
    const [busy, setBusy] = useState<string | null>(null);
    const refresh = (provider: string | null) => {
        setBusy(provider ?? 'all');
        void actions.ingest(provider).finally(() => setBusy(null));
    };

    return (
        <Stack gap={2} sx={{mt: 2}}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="subtitle1" sx={{flex: 1}}>Market indices</Typography>
                <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => refresh(null)}>
                    {busy === 'all' ? 'Refreshing…' : 'Refresh all now'}
                </Button>
            </Stack>
            <Stack direction={{xs: 'column', md: 'row'}} gap={1.5}>
                {catalogue.providers.map(p => (
                    <Box key={p.key} sx={{flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5}}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{fontWeight: 600, flex: 1}}>{p.name}</Typography>
                            <Tag label={p.enabled ? 'On' : 'Off'} color={p.enabled ? 'success' : 'default'}/>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', my: 0.5}}>{p.note}</Typography>
                        <Button size="small" disabled={!p.enabled || busy !== null} onClick={() => refresh(p.key)}>
                            {busy === p.key ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </Box>
                ))}
            </Stack>
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Market index catalogue">
                    <TableHead>
                        <TableRow>
                            <TableCell>On</TableCell>
                            <TableCell>Series</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Unit</TableCell>
                            <TableCell>Latest month</TableCell>
                            <TableCell>Last refresh</TableCell>
                            <TableCell align="right">Used by</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {catalogue.series.map(s => {
                            const [label, color] = FRESHNESS[s.freshness];
                            return (
                                <TableRow key={s.id} sx={{opacity: s.enabled ? 1 : 0.55}}>
                                    <TableCell padding="checkbox">
                                        <Switch size="small" checked={s.enabled} slotProps={{input: {'aria-label': `Use ${s.name}`}}}
                                                onChange={e => void actions.setSeriesEnabled(s.id, s.name, e.target.checked)}/>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{s.name}</Typography>
                                        {s.lastError && <Typography variant="caption" color="error" sx={{display: 'block'}}>{s.lastError}</Typography>}
                                    </TableCell>
                                    <TableCell>{`${s.provider} · ${s.tier}`}</TableCell>
                                    <TableCell>{s.unit}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            {s.latestPeriod?.slice(0, 7) ?? '—'}
                                            <Tag label={label} color={color} solid={false}/>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{s.lastRefreshAt ? WHEN.format(new Date(s.lastRefreshAt)) : '—'}</TableCell>
                                    <TableCell align="right">{s.usedBy ? `${s.usedBy} categor${s.usedBy === 1 ? 'y' : 'ies'}` : '—'}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            <Typography variant="caption" color="text.secondary">
                {`A series switched off is hidden from the Market index tab and skipped by the nightly refresh. `}
                {`${catalogue.uploadedSeries} customer-uploaded series are kept with their datasets.`}
            </Typography>
        </Stack>
    );
};
